import { useState, useEffect } from "react";
import { ListCard } from "../shared";
import { useTheme } from "@mui/material/styles";
import styles from "./mitre.module.scss";
import {
  Box,
  Typography,
  Tooltip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Skeleton,
  Paper,
} from "@mui/material";
import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared";
import * as echarts from "echarts";
import eventEmitter from "../../../components/utils/eventEmitter";

interface TechniqueData {
  id: string;
  code: string;
  name: string;
  threats: number;
}

interface TacticData {
  id: string;
  code: string;
  tactic: string;
  total: number;
  used: number;
  failed_percentage: number;
  techniques: TechniqueData[];
}

interface MitreResponse {
  status: number;
  message: string;
  data: {
    mitre_attack: TacticData[];
  };
}

const Mitre = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [mitreData, setMitreData] = useState<TacticData[]>([]);
  const [viewType, setViewType] = useState<"blocks" | "radar">("blocks");
  const [radarChart, setRadarChart] = useState<echarts.ECharts | null>(null);
  const [trigger, setTrigger] = useState(0); // Used to re-trigger the effect

  const isDarkMode = theme.palette.mode === "dark";

  useEffect(() => {
    const handleFilterSaved = () => {
      setLoading(true);
      setTrigger((prev) => prev + 1); // Changing this will re-trigger the effect
    };

    eventEmitter.on("filterSaved", handleFilterSaved);

    return () => {
      eventEmitter.off("filterSaved", handleFilterSaved);
    };
  }, []);

  useEffect(() => {
    const fetchMitreData = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get<MitreResponse>(
          END_POINTS.DASHBOARD_MITRE
        );
        if (response.status === 200 && response.data.data) {
          setMitreData(response.data.data.mitre_attack);
        }
      } catch (error) {
        // console.error("Error fetching MITRE data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMitreData();
  }, [trigger]);

  useEffect(() => {
    // Initialize radar chart when view type changes to radar
    if (viewType === "radar" && mitreData.length > 0) {
      const chartDom = document.getElementById("mitre-radar-chart");
      if (chartDom) {
        const chart = echarts.init(chartDom);
        setRadarChart(chart);

        const option = {
          radar: {
            indicator: mitreData.map((tactic) => ({
              name: `${tactic.tactic}\n{bold|${tactic.failed_percentage}%}`,
              max: 100,
            })),
            radius: "45%",
            center: ["50%", "50%"],
            splitNumber: 4,
            axisName: {
              color: theme.palette.mode === "dark" ? "#E5E7EB" : "#111827",
              fontSize: 11,
              padding: [0, 15],
              formatter: function (value: string) {
                return value;
              },
              rich: {
                bold: {
                  color: theme.palette.mode === "dark" ? "#E5E7EB" : "#111827",
                  fontSize: 11,
                  fontWeight: "bold",
                  padding: [4, 0, 0, 0],
                },
              },
            },
            splitArea: {
              areaStyle: {
                color:
                  theme.palette.mode === "dark"
                    ? ["rgba(255, 255, 255, 0.02)", "rgba(255, 255, 255, 0.05)"]
                    : ["rgba(0, 0, 0, 0.02)", "rgba(0, 0, 0, 0.05)"],
              },
            },
            axisLine: {
              lineStyle: {
                color:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.1)",
              },
            },
            splitLine: {
              lineStyle: {
                color:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.1)",
              },
            },
          },
          series: [
            {
              type: "radar",
              data: [
                {
                  value: mitreData.map((tactic) => tactic.failed_percentage),
                  name: "Pass Percentage",
                  areaStyle: {
                    color: new echarts.graphic.RadialGradient(0.5, 0.5, 1, [
                      {
                        color: "rgba(220, 38, 38, 0.3)",
                        offset: 0,
                      },
                      {
                        color: "rgba(220, 38, 38, 0.8)",
                        offset: 1,
                      },
                    ]),
                  },
                  lineStyle: {
                    color: "#DC2626",
                  },
                  itemStyle: {
                    color: "#DC2626",
                  },
                },
              ],
            },
          ],
        };

        chart.setOption(option);
      }
    }

    return () => {
      if (radarChart) {
        radarChart.dispose();
      }
    };
  }, [viewType, mitreData, theme.palette.mode]);

  const renderTechniqueBlocks = (
    techniques: TechniqueData[],
    total: number
  ) => {
    // Create an array of total blocks needed
    const blocks = Array(total).fill(null);

    // Fill in the actual techniques where they exist
    techniques.forEach((technique, index) => {
      if (index < total) {
        blocks[index] = technique;
      }
    });

    return blocks.map((technique, index) => {
      const hasThreats = technique?.threats > 0;

      // console.log(technique,"techniquetechniquetechnique")

      return (
       <Tooltip
  key={index}
  title={
    technique ? (
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span>
          <strong>ID:</strong> {technique.code}
        </span>
        <span>
          <strong>Name:</strong> {technique.name}
        </span>
        <span>
          <strong>Threats:</strong> {technique.threats}
        </span>
      </div>
    ) : (
      "No technique"
    )
  }
  arrow
  slotProps={{
    tooltip: {
      sx: {
        backgroundColor: "#fff", 
        color: "#000",           
        boxShadow: 1,
        fontSize: "0.775rem",
      },
    },
    arrow: {
      sx: {
        color: "#fff",
      },
    },
  }}
>
  <Box
    sx={{
      minWidth: 16,
      width: 16,
      height: 16,
      borderRadius: "4px",
      bgcolor: hasThreats
        ? "#B71E31"
        : theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.1)"
        : "#F3F3FE",
      cursor: technique ? "default" : "default",
      flexShrink: 0,
    }}
  />
</Tooltip>

      );
    });
  };

  const renderMitreMatrix = () => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          overflowY: "hidden",
          gap: 0.25,
          m: 0,
          p: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 1,
            m: 0,
            p: 0,
          }}
        >
          {/* Fixed column for tactic names */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.25,
              minWidth: 200,
              flexShrink: 0,
              overflowY: "auto",
              bgcolor: theme.palette.mode === "dark" ? "#191C24" : "white",
              borderRadius: 1,
              "&::-webkit-scrollbar": {
                width: "4px",
              },
              "&::-webkit-scrollbar-track": {
                background:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0, 0, 0, 0.05)",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                background:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.2)"
                    : "rgba(0, 0, 0, 0.2)",
                borderRadius: "10px",
              },
            }}
          >
            {mitreData.map((tactic) => (
              <Box
                key={`label-${tactic.id}`}
                sx={{
                  height: 17,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  py: 1.27,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color:
                      theme.palette.mode === "dark" ? "#E5E7EB" : "#111827",
                    fontWeight: 550,
                    lineHeight: 1.2,
                  }}
                >
                  {tactic.tactic}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                    fontSize: "12px",
                    lineHeight: 1.2,
                  }}
                >
                  {`${tactic.used}/${tactic.total}`}
                </Typography>
              </Box>
            ))}
          </Box>

          {viewType === "blocks" ? (
            /* Scrollable area for technique blocks */
            <Box
              sx={{
                flex: 1,
                overflowX: "auto",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 0.4,
                "&::-webkit-scrollbar": {
                  height: 6,
                  width: 4,
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.05)",
                  borderRadius: 3,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(0, 0, 0, 0.2)",
                  borderRadius: 3,
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.3)"
                        : "rgba(0, 0, 0, 0.3)",
                  },
                },
              }}
            >
              {mitreData.map((tactic) => (
                <Box
                  key={`blocks-${tactic.id}`}
                  sx={{
                    display: "flex",
                    mt: 0.1,

                    gap: 0.7,
                    py: 1,
                    height: 16,
                    alignItems: "center",
                    mb: 0.3,
                  }}
                >
                  {renderTechniqueBlocks(tactic.techniques, tactic.total)}
                </Box>
              ))}
            </Box>
          ) : (
            /* Radar chart container */
            <Box
              sx={{
                flex: 1,
                position: "relative",
                maxWidth: "100%",
                m: 0,
                p: 0,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: theme.palette.mode === "dark" ? "#191C24" : "#FAFAFC",
                borderRadius: 1,
                height: "100%",
              }}
            >
              <div
                id="mitre-radar-chart"
                style={{
                  width: "100%",
                  height: "100%",
                  margin: 0,
                  boxSizing: "border-box",
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ height: 420 }}>
        <Paper
          elevation={0}
          sx={{
            p: 0,
            borderRadius: "4px",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            bgcolor: theme.palette.mode === "dark" ? "#191C24" : "white",
            // boxShadow: theme.palette.mode === "dark"
            //   ? "0px 4px 12px rgba(0, 0, 0, 0.3)"
            //   : "0px 4px 12px rgba(0, 0, 0, 0.1)",
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.1)"
            }`,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              borderBottom: `1px solid ${theme.palette.divider}`,
              height: 45,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              borderRadius: 3,
              bgcolor: theme.palette.mode === "dark" ? "#191C24" : "#FFFFFF",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Skeleton variant="text" width={150} height={24} />
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Skeleton
                variant="rectangular"
                width={40}
                height={32}
                sx={{ borderRadius: 1 }}
              />
              <Skeleton
                variant="rectangular"
                width={40}
                height={32}
                sx={{ borderRadius: 1 }}
              />
            </Box>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              p: 2,
              display: "flex",
              gap: 2,
            }}
          >
            {/* Left side list skeleton */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                minWidth: 200,
                flexShrink: 0,
              }}
            >
              {[1, 2, 3, 4, 5, 6].map((index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Skeleton variant="text" width={120} height={20} />
                  <Skeleton variant="text" width={40} height={20} />
                </Box>
              ))}
            </Box>

            {/* Right side blocks skeleton */}
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {[1, 2, 3, 4, 5, 6].map((index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                  }}
                >
                  {[...Array(12)].map((_, blockIndex) => (
                    <Skeleton
                      key={blockIndex}
                      variant="rectangular"
                      width={16}
                      height={16}
                      sx={{ borderRadius: 1 }}
                    />
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: 420, // Controllable fixed height
        bgcolor: theme.palette.mode === "dark" ? "#191C24" : "white",
        borderRadius: "6px ",
        // boxShadow: theme.palette.mode === "dark"
        //   ? "0px 4px 12px rgba(0, 0, 0, 0.3)"
        //   : "0px 4px 12px rgba(0, 0, 0, 0.1)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        border: `1px solid ${
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.1)"
            : "rgba(0, 0, 0, 0.1)"
        }`,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          height: 45,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "13px" }}>
          MITRE ATT&CK
        </Typography>
        <Box
          sx={{
            display: "flex",
            fontSize: "13px",
            alignItems: "center",
            gap: 2,
          }}
        >
          {viewType === "blocks" && (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}></Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}></Box>
            </>
          )}
          <ToggleButtonGroup
            value={viewType}
            exclusive
            onChange={(e, value) => value && setViewType(value)}
            size="small"
          >
            <ToggleButton
              value="blocks"
              sx={{ minWidth: 40, px: 1, cursor: "pointer" }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "2px",
                }}
              >
                {[...Array(4)].map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: "100%",
                      height: "100%",
                      bgcolor: "currentColor",
                      borderRadius: "1px",
                    }}
                  />
                ))}
              </Box>
            </ToggleButton>
            <ToggleButton
              value="radar"
              sx={{ minWidth: 40, px: 1, cursor: "pointer" }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  border: "2px solid currentColor",
                  borderRadius: "50%",
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    width: "60%",
                    height: "60%",
                    border: "2px solid currentColor",
                    borderRadius: "50%",
                    transform: "translate(-50%, -50%)",
                  },
                }}
              />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          p: 2,
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              gap: 0.25,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1,
                height: "100%",
              }}
            >
              {/* Fixed column for tactic names */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.25,
                  minWidth: 200,
                  flexShrink: 0,
                  overflowY: "auto",
                  bgcolor: theme.palette.mode === "dark" ? "#191C24" : "white",
                  borderRadius: 1,
                  "&::-webkit-scrollbar": {
                    width: "4px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.05)"
                        : "rgba(0, 0, 0, 0.05)",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.2)"
                        : "rgba(0, 0, 0, 0.2)",
                    borderRadius: "10px",
                  },
                }}
              >
                {mitreData.map((tactic) => (
                  <Box
                    key={`label-${tactic.id}`}
                    sx={{
                      height: 17,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      py: 1.27,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          theme.palette.mode === "dark" ? "#E5E7EB" : "#111827",
                        fontWeight: 550,
                        lineHeight: 1.2,
                      }}
                    >
                      {tactic.tactic}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 500,
                        fontSize: "12px",
                        lineHeight: 1.2,
                      }}
                    >
                      {`${tactic.used}/${tactic.total}`}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {viewType === "blocks" ? (
                /* Scrollable area for technique blocks */
                <Box
                  sx={{
                    flex: 1,
                    overflowX: "auto",
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.4,
                    "&::-webkit-scrollbar": {
                      height: 6,
                      width: 4,
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.05)",
                      borderRadius: 3,
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.2)"
                          : "rgba(0, 0, 0, 0.2)",
                      borderRadius: 3,
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.3)"
                            : "rgba(0, 0, 0, 0.3)",
                      },
                    },
                  }}
                >
                  {mitreData.length === 0 ? (
                     <Box sx={{
                                  marginLeft: "15%",
                                  marginTop: "30%",
                                  color: theme.palette.text.secondary
                                }}>
                    <Typography>No data Found</Typography>
                    </Box>
                  ) : (
                    mitreData.map((tactic) => (
                      <Box
                        key={`blocks-${tactic.id}`}
                        sx={{
                          display: "flex",
                          mt: 0.1,
                          gap: 0.7,
                          py: 1,
                          height: 16,
                          alignItems: "center",
                          mb: 0.3,
                        }}
                      >
                        {renderTechniqueBlocks(tactic.techniques, tactic.total)}
                      </Box>
                    ))
                  )}
                </Box>
              ) : (
                /* Radar chart container */
                <Box
                  sx={{
                    flex: 1,
                    position: "relative",
                    maxWidth: "100%",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor:
                      theme.palette.mode === "dark" ? "#191C24" : "#FAFAFC",
                    borderRadius: 1,
                  }}
                >
                  <div
                    id="mitre-radar-chart"
                    style={{
                      width: "100%",
                      height: "100%",
                      margin: 0,
                      boxSizing: "border-box",
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Mitre;
